import { useMemo } from 'react';

interface NextActionInput {
  completedTasks: string[];
  readinessPercentage: number;
}

export interface NextAction {
  message: string;
  actionText: string;
  actionUrl: string;
  emoji: string;
}

export const getNextAction = (
  completedTasks: string[],
  readinessPercentage: number
): NextAction => {
  if (!completedTasks.includes('complete_profile')) {
    return {
      message:
        'Complete your profile to earn 30 XP and help us personalize your learning experience!',
      actionText: 'Complete Profile',
      actionUrl: '/profile',
      emoji: '👤',
    };
  }

  if (!completedTasks.includes('take_skills_assessment')) {
    return {
      message:
        'Take the Skills Assessment to earn 100 XP and get your personalized AI learning plan!',
      actionText: 'Start Assessment',
      actionUrl: '/skills-assessment',
      emoji: '📊',
    };
  }

  if (!completedTasks.includes('complete_first_free_course')) {
    return {
      message: 'Complete your first free course to earn 50 XP and start your AI journey!',
      actionText: 'Browse Courses',
      actionUrl: '/courses',
      emoji: '📚',
    };
  }

  if (!completedTasks.includes('upgrade_account')) {
    return {
      message:
        "You've completed your free courses. Upgrade your account for 100 XP and unlock premium content!",
      actionText: 'View Pricing',
      actionUrl: '/pricing',
      emoji: '⭐',
    };
  }

  if (!completedTasks.includes('join_discord')) {
    return {
      message: "Haven't joined the community yet? Say hi on Discord for a quick 20 XP!",
      actionText: 'Join Discord',
      actionUrl: '/community',
      emoji: '🚀',
    };
  }

  if (!completedTasks.includes('complete_first_paid_course')) {
    return {
      message: 'Start your first paid course to earn 20 XP and deepen your AI knowledge!',
      actionText: 'Explore Courses',
      actionUrl: '/courses',
      emoji: '🎯',
    };
  }

  if (readinessPercentage < 30) {
    return {
      message: "You're just getting started! Complete more tasks to boost your AI Readiness score.",
      actionText: 'View Dashboard',
      actionUrl: '/dashboard',
      emoji: '🌱',
    };
  }

  if (readinessPercentage >= 30 && readinessPercentage < 60) {
    return {
      message: "Great progress! You're improving your AI skills. Keep going!",
      actionText: 'Continue Learning',
      actionUrl: '/courses',
      emoji: '📈',
    };
  }

  if (readinessPercentage >= 60 && readinessPercentage < 90) {
    return {
      message: "You're becoming confident with AI! Just a few more steps to reach expert level.",
      actionText: 'View Progress',
      actionUrl: '/progress',
      emoji: '💪',
    };
  }

  return {
    message: "Amazing! You're almost at 100% AI Readiness. You're an AI expert!",
    actionText: 'Share Your Success',
    actionUrl: '/community',
    emoji: '🎉',
  };
};

export const useNextAction = ({ completedTasks, readinessPercentage }: NextActionInput) =>
  useMemo(() => getNextAction(completedTasks, readinessPercentage), [completedTasks, readinessPercentage]);

